<template>
  <div>
    <BobForm
      :formRef.sync="formRef"
      :fields="displayFields"
      :model="formData"
      :rules="formRules"
      label-position="top"
    />
    <CarrierNumCameraScanDialog
      v-if="cameraScanVisible"
      @scan="onCarrierNumScan"
      @close="cameraScanVisible = false"
    />
    <CarrierNumScannerGunDialog
      v-if="gunScanVisible"
      @scan="onCarrierNumScan"
      @close="gunScanVisible = false"
    />
  </div>
</template>

<script>
import { useBaseForm } from '@/use/useForm'
import { emailRules, emailRulesNoRequired, isDigitRules, lengthRules, noEmptyRules } from '@/validation'
import { computed, defineComponent, onMounted, provide, ref, watch } from 'vue'
import BobForm from './BobForm.vue'
import CarrierNumCameraScanDialog from './CarrierNumCameraScanDialog.vue'
import CarrierNumScannerGunDialog from './CarrierNumScannerGunDialog.vue'
import { useInvoiceCarrier } from '@/use/useInvoiceCarrier'
import { filter, includes, map } from 'lodash'
import { carrierTypeConfig } from '@/config/invoice'

const invoiceTypeConfig = {
  none: { value: 'none', label: '不開發票' },
  C: { value: 'C', label: '二聯式發票' },
  B: { value: 'B', label: '三聯式發票' },
  carrier: { value: 'carrier', label: '手機載具' },
}

export default defineComponent({
  name: 'BobInvoiceForm',
  components: {
    BobForm,
    CarrierNumCameraScanDialog,
    CarrierNumScannerGunDialog,
  },
  props: {
    source: { type: String, default: 'appointmentCheckout' },
    typeSelectorMode: {
      type: String,
      default: 'select',
      validator: (value) => ['select', 'radioBorder'].includes(value),
    },
    optionalFields: {
      type: Array,
      default: () => [],
      validator: (value) => value.every((field) => includes(['email', 'companyTitle'], field)),
    },
    invoiceTypes: {
      type: Array,
      default: () => ['C', 'B', 'carrier'],
      validator: (value) => value.length > 0 && value.every((type) => includes(Object.keys(invoiceTypeConfig), type)),
    },
    carrierTypes: {
      type: Array,
      default: () => ['0', '1'],
      validator: (value) => value.length > 0 && value.every((type) => includes(['0', '1'], type)),
    },
    carrierTypeFixed: {
      type: String,
      default: undefined,
      validator: (value) => includes(['0', '1'], value),
    },
    value: {
      type: Object,
      default: () => ({
        type: 'C', // 發票類型
        companyTitle: undefined, // 發票Email
        taxId: undefined, // 公司行號抬頭
        email: undefined, // 統一編號
      }),
    },
  },
  setup (props, { emit }) {
    const { getCarrierNumPlaceholder, getCarrierNumRules } = useInvoiceCarrier(props.source)
    const { formData, formRef, initFormData, checkForm } = useBaseForm()
    const invoiceTypeOptions = computed(() => {
      return map(props.invoiceTypes, (type) => invoiceTypeConfig[type])
    })

    const carrierTypeOptions = computed(() => {
      return map(props.carrierTypes, (type) => ({
        value: carrierTypeConfig[type]?.value,
        name: carrierTypeConfig[type]?.label,
      }))
    })

    const cameraScanVisible = ref(false)
    const gunScanVisible = ref(false)

    const onCarrierNumScan = (val) => {
      formData.carrierNum = val
      cameraScanVisible.value = false
      gunScanVisible.value = false
    }

    initFormData({
      type: 'C',
      companyTitle: undefined,
      taxId: undefined,
      email: undefined,
      carrierType: null,
      carrierNum: null,
    })

    const displayFields = computed(() => {
      const omitFields = []
      const invoiceTypeField = props.typeSelectorMode === 'radioBorder'
        ? {
          prop: 'type',
          label: '發票種類',
          body: {
            component: 'BaseElRadioGroup',
            props: {
              class: 'flex flex-col',
              style: {
                gap: '8px',
              },
            },
            slots: {
              default: invoiceTypeOptions.value.map((item) => ({
                component: 'BaseElRadio',
                props: {
                  label: item.value,
                  border: true,
                  style: {
                    width: '100%',
                    marginLeft: 0,
                    marginRight: 0,
                  },
                },
                render: () => item.label,
              })),
            },
          },
        }
        : {
          prop: 'type',
          label: '發票種類',
          body: {
            component: () => import('@/components/Select/BaseDataSelect.vue'),
            props: {
              value: formData.type,
              clearable: false,
              placeholder: '選擇發票類型',
              filterable: false,
              objKey: 'value',
              dataList: invoiceTypeOptions.value.map(({ value, label }) => ({
                value,
                name: label,
              })),
            },
            on: {
              'update:value': (val) => {
                formData.type = val
              },
            },
          },
        }
      const fields = [
        invoiceTypeField,
        // 公司行號抬頭
        {
          prop: 'companyTitle',
          label: '公司行號抬頭',
          body: {
            component: 'BaseElInput',
          },
        },
        // 統一編號
        {
          prop: 'taxId',
          label: '統一編號',
          body: {
            component: 'BaseElInput',
            props: {
              type: 'number',
              name: 'taxId',
              maxlength: 8,
              class: '!w-full',
            },
          },
        },
        // 電子信箱
        {
          prop: 'email',
          label: '電子信箱',
          body: {
            component: 'BaseElInput',
            props: {
              type: 'email',
              name: 'email',
              class: '!w-full',
            },
          },
        },
        // 載具類型
        {
          prop: 'carrierType',
          label: '載具類型',
          body: {
            component: () => import('@/components/Select/BaseDataSelect.vue'),
            props: {
              value: formData.carrierType,
              placeholder: '請選擇載具類型',
              filterable: false,
              objKey: 'value',
              disabled: !!props.carrierTypeFixed,
              dataList: carrierTypeOptions.value,
            },
            on: {
              'update:value': (val) => { formData.carrierType = val },
            },
          },
        },
        // 載具號碼
        {
          label: '載具號碼',
          props: {
            prop: 'carrierNum',
          },
          body: {
            component: 'div',
            props: {
              class: 'w-full flex gap-[4px]',
            },
            slots: {
              default: [
                {
                  component: 'BaseElInput',
                  props: {
                    placeholder: getCarrierNumPlaceholder(formData.carrierType),
                    class: '!w-full',
                    value: formData.carrierNum,
                  },
                  on: {
                    input: (val) => { formData.carrierNum = val },
                  },
                },
                {
                  component: 'span',
                  props: { class: 'cursor-pointer flex items-center' },
                  on: {
                    click: () => { cameraScanVisible.value = true },
                  },
                  slots: {
                    default: {
                      component: () => import('@/components/SVGIcon.vue'),
                      props: { icon: 'common-scanner', width: '22', height: '22' },
                    },
                  },
                },
                {
                  component: 'span',
                  props: { class: 'cursor-pointer flex items-center' },
                  on: {
                    click: () => { gunScanVisible.value = true },
                  },
                  slots: {
                    default: {
                      component: () => import('@/components/SVGIcon.vue'),
                      props: { icon: 'common-scanner_gun', width: '22', height: '22' },
                    },
                  },
                },
              ],
            },
          },
        },
      ]

      if (formData.type === 'C') {
        omitFields.push('companyTitle', 'taxId', 'carrierType', 'carrierNum')
      } else if (formData.type === 'B') {
        omitFields.push('carrierType', 'carrierNum')
      } else if (formData.type === 'carrier') {
        omitFields.push('companyTitle', 'taxId')
      } else if (formData.type === 'none') {
        omitFields.push('companyTitle', 'taxId', 'email', 'carrierType', 'carrierNum')
      }

      return filter(fields, (field) => {
        return !includes(omitFields, field.prop || field.props.prop)
      })
    })

    const formRules = computed(() => {
      const carrierNumRules = getCarrierNumRules(formData.carrierType)
      const companyTitleRules = includes(props.optionalFields, 'companyTitle') ? [] : [noEmptyRules()]
      const emailRulesList = includes(props.optionalFields, 'email') ? [emailRulesNoRequired()] : [noEmptyRules(), emailRules()]

      return {
        type: [noEmptyRules()],
        companyTitle: companyTitleRules,
        taxId: [noEmptyRules(), lengthRules(8, 8), isDigitRules()],
        email: emailRulesList,
        carrierType: [noEmptyRules()],
        carrierNum: carrierNumRules,
      }
    })

    const validateForm = async () => {
      return await checkForm(formRef.value)
    }

    watch(() => formData.type, (type) => {
      if (type !== 'carrier') {
        formData.carrierType = null
        formData.carrierNum = null
      } else if (props.carrierTypeFixed) {
        formData.carrierType = props.carrierTypeFixed
      }
    })

    watch(formData, (newVal) => {
      emit('update', newVal)
    }, { deep: true })

    provide('formData', formData)

    onMounted(() => {
      if (props.value) {
        formData.type = includes(props.invoiceTypes, props.value.type)
          ? props.value.type
          : props.invoiceTypes[0]
        formData.email = props.value.email
        formData.companyTitle = props.value.companyTitle
        formData.taxId = props.value.taxId
        formData.carrierType = props.carrierTypeFixed ?? props.value.carrierType ?? null
        formData.carrierNum = props.value.carrierNum ?? null
      } else {
        formData.type = props.invoiceTypes[0]
      }
    })

    return {
      formData,
      formRef,
      displayFields,
      formRules,
      validateForm,
      cameraScanVisible,
      gunScanVisible,
      onCarrierNumScan,
    }
  },
})
</script>

<style lang="postcss" scoped>
</style>
